import { useMemo, useState } from "react";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { supabase } from "@/integrations/supabase/client";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Pencil, Trash2, Eye, CheckCircle } from "lucide-react";
import QuestionEditor from "./QuestionEditor";
import { DataTable } from "@/components/common/DataTable";
import { ListToolbar } from "@/components/common/ListToolbar";
import { DataPagination } from "@/components/common/DataPagination";
import { usePaginatedQuery } from "@/hooks/usePaginatedQuery";
import { useUrlState } from "@/lib/urlState";
import { queryKeys } from "@/lib/queryKeys";
import { exportRowsAsCsv } from "@/lib/csv";

interface Profile {
  id: string;
  first_name: string | null;
  last_name: string | null;
}

interface QuestionOption {
  id: string;
  text: string;
  is_correct: boolean;
}

interface ReportedQuestionData {
  id: string;
  text: string;
  explanation: string | null;
  options: QuestionOption[];
}

interface QuestionReport {
  id: string;
  question_id: string;
  user_id: string | null;
  concern: string;
  created_at: string;
  status: string;
  grade: string | null;
  subject: string | null;
  question?: ReportedQuestionData | null;
}

const TABS = ["pending", "resolved", "rejected", "all"] as const;
type TabKey = (typeof TABS)[number];

const ReportedQuestions = () => {
  const qc = useQueryClient();
  const url = useUrlState();
  const search = url.get("q") ?? "";
  const tab = (url.get("tab") as TabKey) ?? "pending";

  const [viewReport, setViewReport] = useState<QuestionReport | null>(null);
  const [reportToDelete, setReportToDelete] = useState<QuestionReport | null>(null);
  const [editingQuestion, setEditingQuestion] = useState<ReportedQuestionData | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const paginated = usePaginatedQuery<QuestionReport>({
    queryKey: queryKeys.reportedQuestions.list({
      page: 0,
      pageSize: 25,
      filters: { tab, search },
    }),
    table: "question_reports",
    select:
      "*,question:questions(id,text,explanation,options(id,text,is_correct))",
    syncToUrl: true,
    orderBy: { column: "created_at", ascending: false },
    filters: (q) => {
      let next = q;
      if (tab !== "all") next = next.eq("status", tab);
      if (search) next = next.or(`concern.ilike.%${search}%,subject.ilike.%${search}%`);
      return next;
    },
  });

  // Hydrate reporter profiles for visible rows
  const userIds = useMemo(
    () => Array.from(new Set(paginated.rows.map((r) => r.user_id).filter(Boolean) as string[])),
    [paginated.rows],
  );
  const profilesQuery = useQuery({
    queryKey: ["profiles", "byIds", userIds],
    enabled: userIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("id,first_name,last_name")
        .in("id", userIds);
      if (error) throw error;
      const map = new Map<string, Profile>();
      (data ?? []).forEach((p) => map.set(p.id, p as Profile));
      return map;
    },
  });

  const getFullName = (report: QuestionReport) => {
    if (!report.user_id) return "Anonymous";
    const p = profilesQuery.data?.get(report.user_id);
    if (!p) return "—";
    return `${p.first_name ?? ""} ${p.last_name ?? ""}`.trim() || "Anonymous";
  };

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["reportedQuestions"] });

  const markReportAs = async (reportId: string, status: string) => {
    setIsProcessing(true);
    try {
      const { error } = await supabase
        .from("question_reports")
        .update({ status: status as "pending" | "in_review" | "resolved" | "rejected" })
        .eq("id", reportId);
      if (error) throw error;
      toast.success(`Report marked as ${status}`);
      setViewReport(null);
      invalidate();
    } catch (err) {
      console.error(err);
      toast.error(`Failed to mark report as ${status}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeleteQuestion = async () => {
    if (!reportToDelete) return;
    setIsProcessing(true);
    try {
      const { error: optsErr } = await supabase
        .from("options")
        .delete()
        .eq("question_id", reportToDelete.question_id);
      if (optsErr) throw optsErr;

      const { error } = await supabase
        .from("questions")
        .delete()
        .eq("id", reportToDelete.question_id);
      if (error) throw error;

      await supabase
        .from("question_reports")
        .update({ status: "resolved" })
        .eq("question_id", reportToDelete.question_id);

      toast.success("Question deleted");
      setReportToDelete(null);
      invalidate();
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete question");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSaveQuestion = async (questionData: any) => {
    setIsProcessing(true);
    try {
      const { error } = await supabase
        .from("questions")
        .update({
          text: questionData.text,
          explanation: questionData.explanation,
        })
        .eq("id", questionData.id);
      if (error) throw error;

      const { error: clearErr } = await supabase
        .from("options")
        .delete()
        .eq("question_id", questionData.id);
      if (clearErr) throw clearErr;

      const optionsToInsert = questionData.options.map((option: any) => ({
        question_id: questionData.id,
        text: option.text,
        is_correct: option.is_correct,
      }));
      if (optionsToInsert.length > 0) {
        const { error: insErr } = await supabase.from("options").insert(optionsToInsert);
        if (insErr) throw insErr;
      }

      await supabase
        .from("question_reports")
        .update({ status: "resolved" })
        .eq("question_id", questionData.id)
        .eq("status", "pending");

      toast.success("Question updated and reports resolved");
      setEditingQuestion(null);
      invalidate();
    } catch (err) {
      console.error(err);
      toast.error("Failed to update question");
    } finally {
      setIsProcessing(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const map: Record<string, string> = {
      pending: "bg-yellow-100 text-yellow-800",
      resolved: "bg-green-100 text-green-800",
      rejected: "bg-red-100 text-red-800",
    };
    return (
      <Badge variant="outline" className={map[status] ?? ""}>
        {status}
      </Badge>
    );
  };

  const columns = useMemo<ColumnDef<QuestionReport, unknown>[]>(
    () => [
      {
        id: "question",
        header: "Question",
        cell: ({ row }) => {
          const q = row.original.question;
          if (!q) return <span className="text-muted-foreground italic">Question deleted</span>;
          return (
            <div className="truncate max-w-xs">
              {q.text.length > 70 ? `${q.text.substring(0, 70)}…` : q.text}
            </div>
          );
        },
      },
      {
        id: "reporter",
        header: "Reported By",
        cell: ({ row }) => getFullName(row.original),
      },
      { accessorKey: "grade", header: "Grade" },
      { accessorKey: "subject", header: "Subject" },
      {
        accessorKey: "created_at",
        header: "Date",
        cell: ({ row }) => new Date(row.original.created_at).toLocaleDateString(),
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => getStatusBadge(row.original.status),
      },
      {
        id: "actions",
        header: "Actions",
        cell: ({ row }) => {
          const report = row.original;
          return (
            <div className="flex space-x-2">
              <Button variant="ghost" size="sm" onClick={() => setViewReport(report)}>
                <Eye className="h-4 w-4" />
                <span className="sr-only">View</span>
              </Button>
              {report.question && report.status === "pending" && (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditingQuestion(report.question!)}
                  >
                    <Pencil className="h-4 w-4" />
                    <span className="sr-only">Edit</span>
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive"
                    onClick={() => setReportToDelete(report)}
                  >
                    <Trash2 className="h-4 w-4" />
                    <span className="sr-only">Delete</span>
                  </Button>
                </>
              )}
            </div>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [profilesQuery.data],
  );

  return (
    <div>
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Reported Questions</CardTitle>
          <CardDescription>
            Review and manage questions that users have reported as problematic
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Tabs value={tab} onValueChange={(v) => url.set({ tab: v, page: 0 })}>
            <TabsList>
              <TabsTrigger value="pending">Pending</TabsTrigger>
              <TabsTrigger value="resolved">Resolved</TabsTrigger>
              <TabsTrigger value="rejected">Rejected</TabsTrigger>
              <TabsTrigger value="all">All</TabsTrigger>
            </TabsList>
          </Tabs>

          <ListToolbar
            search={search}
            onSearchChange={(v) => url.set({ q: v || null, page: 0 })}
            searchPlaceholder="Search concern or subject…"
            onExportCsv={() =>
              exportRowsAsCsv(
                "reported-questions.csv",
                paginated.rows as unknown as Record<string, unknown>[],
              )
            }
          />

          <DataTable
            columns={columns}
            data={paginated.rows}
            isLoading={paginated.isLoading}
            isError={paginated.isError}
            onRetry={paginated.refetch}
            emptyTitle="No reports found"
            emptyDescription={
              tab === "all" ? "No reports yet." : `No ${tab} reports.`
            }
            getRowId={(row) => row.id}
          />

          <DataPagination
            page={paginated.page}
            pageSize={paginated.pageSize}
            total={paginated.total}
            pageCount={paginated.pageCount}
            onPageChange={paginated.setPage}
            onPageSizeChange={paginated.setPageSize}
            isFetching={paginated.isFetching}
          />
        </CardContent>
      </Card>

      {/* View dialog */}
      <Dialog open={!!viewReport} onOpenChange={(open) => !open && setViewReport(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Report Details</DialogTitle>
          </DialogHeader>

          {viewReport && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium mb-2">Reported Question</h3>
                {viewReport.question ? (
                  <Card>
                    <CardContent className="pt-6">
                      <p className="mb-4">{viewReport.question.text}</p>
                      <div className="space-y-2 mb-4">
                        <h4 className="font-medium">Options:</h4>
                        {viewReport.question.options.map((option) => (
                          <div key={option.id} className="flex items-start gap-2">
                            {option.is_correct ? (
                              <CheckCircle className="text-green-600 h-5 w-5 mt-0.5" />
                            ) : (
                              <div className="h-5 w-5" />
                            )}
                            <p className={option.is_correct ? "font-medium" : ""}>
                              {option.text}
                            </p>
                          </div>
                        ))}
                      </div>
                      {viewReport.question.explanation && (
                        <div>
                          <h4 className="font-medium mb-1">Explanation:</h4>
                          <p className="text-muted-foreground">
                            {viewReport.question.explanation}
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ) : (
                  <Card>
                    <CardContent className="py-4">
                      <p className="text-muted-foreground italic">
                        This question has been deleted
                      </p>
                    </CardContent>
                  </Card>
                )}
              </div>

              <div>
                <h3 className="text-lg font-medium mb-2">Report Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Reported By</p>
                    <p className="font-medium">{getFullName(viewReport)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Date Reported</p>
                    <p className="font-medium">
                      {new Date(viewReport.created_at).toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Grade</p>
                    <p className="font-medium">{viewReport.grade}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Subject</p>
                    <p className="font-medium">{viewReport.subject}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-sm text-muted-foreground">Concern</p>
                    <p className="font-medium">{viewReport.concern}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Status</p>
                    <p className="font-medium">{getStatusBadge(viewReport.status)}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            {viewReport && viewReport.status === "pending" && viewReport.question && (
              <>
                <Button
                  variant="outline"
                  onClick={() => markReportAs(viewReport.id, "rejected")}
                  disabled={isProcessing}
                >
                  Reject Report
                </Button>
                <Button
                  onClick={() => {
                    setEditingQuestion(viewReport.question!);
                    setViewReport(null);
                  }}
                  disabled={isProcessing}
                >
                  Edit Question
                </Button>
              </>
            )}
            <DialogClose asChild>
              <Button variant="secondary">Close</Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete dialog */}
      <Dialog
        open={!!reportToDelete}
        onOpenChange={(open) => !open && setReportToDelete(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Question</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this question? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setReportToDelete(null)}
              disabled={isProcessing}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteQuestion}
              disabled={isProcessing}
            >
              {isProcessing ? "Deleting…" : "Delete Question"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {editingQuestion && (
        <QuestionEditor
          question={editingQuestion}
          onSave={handleSaveQuestion}
          onCancel={() => setEditingQuestion(null)}
        />
      )}
    </div>
  );
};

export default ReportedQuestions;
